import React from 'react';

import { useInvertedLocalize } from '../../../../../shared/hooks';

import './ComplianceScoreChip.css';

interface ComplianceScoreChipProps {
  score: number;
  label: string;
}

/**
 * Displays a circular progress indicator chip representing a compliance score percentage.
 * @param {object} props - Component props
 * @param {number} props.score - Compliance score value (0–100) used to calculate the progress arc
 * @param {string} props.label - Text label displayed beside the score meter
 * @returns {React.ReactElement} A chip with an SVG circular progress meter and label
 */
function ComplianceScoreChip({
  score,
  label,
}: ComplianceScoreChipProps): React.ReactElement {
  const localize = useInvertedLocalize();
  // Calculate the circular progress arc
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className='complianceScoreChip'>
      <div className='complianceScoreChipMeter'>
        <svg className='complianceScoreChipMeterSvg' viewBox='0 0 32 32'>
          {/* Background circle */}
          <circle
            cx='16'
            cy='16'
            r='14'
            fill='none'
            stroke='#373840'
            strokeWidth='4'
            strokeLinecap='round'
          />
          {/* Progress circle */}
          <circle
            cx='16'
            cy='16'
            r='14'
            fill='none'
            stroke='#f6a6a4'
            strokeWidth='4'
            strokeLinecap='round'
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform='rotate(-90 16 16)'
            style={{ transition: 'stroke-dashoffset 0.3s ease' }}
          />
        </svg>
        <span className='complianceScoreChipPercentage'>
          {score}
          {localize('%')}
        </span>
      </div>
      <span className='complianceScoreChipLabel'>{label}</span>
    </div>
  );
}

export default ComplianceScoreChip;
